// Setup › Membership plans on the SettingsPage pattern (BL-189): the plans the company sells
// (membershipTypes.list), each with its price, term, the visits it includes, its member discount and how
// many members hold it; then how memberships renew (RULE_SECTIONS). As Memberships.tsx's Plans draws
// them in Blueprint, with nothing changed: Add a plan and each plan's Change open a FormSheet where the
// inline panels were; the owner or a manager changes them (memberships.manage). A change is for sales
// from now on: members keep the terms they bought. While the company mirrors its import source the plans
// read, with the line (BL-088). Riverstone keeps Memberships' Plans.
import { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Link, SelectField, TextField } from '@flowology/ui-web/fl';
import { FormSheet, SettingRow, SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { refusal } from '../history/customer-edit-model.ts';
import {
  CANCEL_REFUND_WORDS,
  discountText,
  everyPage,
  NOT_SET,
  PLAN_PERMS,
  termText,
  type CancelRefund,
} from '../memberships/model.ts';
import {
  planForm,
  planProblems,
  planSavedText,
  planUpdate,
  type EditablePlan,
} from '../memberships/plan-edit-model.ts';
import { money } from '../review/review-model.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { BP_LEDE } from './ledes.ts';
import { settingsPage } from './parts.ts';
import { settingsRights } from './settings-model.ts';
import { SetupRuleSections } from './SetupRules.tsx';

type JobTypeName = { id: string; name: string };
type ServiceItem = { id: string; name: string; priceCents: number };

const CANCEL_OPTIONS = (Object.keys(CANCEL_REFUND_WORDS) as CancelRefund[]).map((k) => ({
  value: k,
  label: CANCEL_REFUND_WORDS[k],
}));

/** "2 visits: Furnace tune-up" or "No visits": what a plan's term includes. */
const includesText = (p: { visitsPerTerm: number | null; visitJobTypeName: string | null }) =>
  p.visitsPerTerm === null
    ? `visits ${NOT_SET.toLowerCase()}`
    : p.visitsPerTerm === 0
      ? 'No visits'
      : `${p.visitsPerTerm} ${p.visitsPerTerm === 1 ? 'visit' : 'visits'}${p.visitJobTypeName ? `: ${p.visitJobTypeName}` : ''}`;

/** A plan's facts on one line: "$199.00 · 1 year · 1 visit: Annual maintenance · 10% off · 2 members". */
const factsOf = (p: EditablePlan) =>
  [
    p.priceCents === null ? `price ${NOT_SET.toLowerCase()}` : money(p.priceCents),
    p.termMonths === null ? `term ${NOT_SET.toLowerCase()}` : termText(p.termMonths),
    includesText(p),
    discountText(p.discountPct) ?? 'no discount',
    `${p.activeMembers} ${p.activeMembers === 1 ? 'member' : 'members'}`,
  ].join(' · ');

/** The job types an included visit can be. */
function useJobTypes(): JobTypeName[] {
  const [types, setTypes] = useState<JobTypeName[]>([]);
  useEffect(() => {
    client
      .call<{ jobTypes: JobTypeName[] }>('jobTypes.list', {})
      .then((r) => setTypes(r.jobTypes))
      // the picker stays empty; a plan with visits can't be saved without one
      .catch(() => undefined);
  }, []);
  return types;
}

export function MembershipPlansPage({ nav }: { nav: NavProps }) {
  const held = nav.permissions ?? [];
  const rights = settingsRights(held);
  // the import source's while the company mirrors it (BL-088): read, never changed
  const mirroring = useMirror();
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('plans').label ?? 'Membership plans',
            lede: BP_LEDE.plans,
          }}
        >
          <SetupRuleSections page="plans" top rights={rights} />
          <Plans canEdit={!mirroring && PLAN_PERMS.some((p) => held.includes(p))} mirroring={mirroring} />
          <SetupRuleSections page="plans" rights={rights} />
        </SettingsPage>
      </main>
    </div>
  );
}

function Plans({ canEdit, mirroring }: { canEdit: boolean; mirroring: boolean }) {
  const [plans, setPlans] = useState<EditablePlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<EditablePlan | 'new' | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const load = useCallback(() => {
    client
      .call<{ plans: EditablePlan[] }>('membershipTypes.list', {})
      .then((r) => {
        setPlans(r.plans);
        setError(null);
      })
      .catch((e: unknown) => setError(refusal(e)));
  }, []);
  useEffect(load, [load]);
  const saved = (words: string) => {
    setSaid(words);
    load();
  };
  return (
    <>
      <SettingsSection
        title="Plans"
        {...(canEdit
          ? {
              action: (
                <Button variant="primary" onPress={() => setOpen('new')}>
                  Add a plan
                </Button>
              ),
            }
          : {})}
        {...(mirroring || said
          ? {
              note: (
                <>
                  {mirroring ? MIRROR_LINE : null}
                  {mirroring && said ? ' ' : null}
                  {said ? <span role="status">{said}</span> : null}
                </>
              ),
            }
          : {})}
        status={plans ? (plans.length ? 'ready' : 'empty') : error ? 'failed' : 'loading'}
        emptyText={`No plans yet.${canEdit ? ' Add the first one.' : ' The owner or a manager adds them.'}`}
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      >
        {plans?.map((p) => {
          const tags = [!p.active && 'Not sold now', p.bare && 'Set its term, visits and price'];
          const tag = tags.filter(Boolean).join(' · ');
          return (
            <SettingRow
              key={p.id}
              name={p.name}
              {...(tag ? { tag } : {})}
              help={factsOf(p)}
              control={
                canEdit ? (
                  <Link label={`Change ${p.name}`} onPress={() => setOpen(p)}>
                    Change
                  </Link>
                ) : null
              }
            />
          );
        })}
      </SettingsSection>
      {/* over the page: a section's body is its empty or failed line until it has rows */}
      {open === 'new' && <NewPlanSheet onSaved={saved} onClose={() => setOpen(null)} />}
      {open && open !== 'new' && (
        <ChangePlanSheet key={open.id} plan={open} onSaved={saved} onClose={() => setOpen(null)} />
      )}
    </>
  );
}

/** Changes a plan for sales from now on (membershipTypes.update): only what changed is sent. */
function ChangePlanSheet({
  plan,
  onSaved,
  onClose,
}: {
  plan: EditablePlan;
  onSaved: (said: string) => void;
  onClose: () => void;
}) {
  const types = useJobTypes();
  const [first] = useState(() => planForm(plan));
  const [f, setF] = useState(first);
  const keep = plan.activeMembers
    ? ` ${plan.activeMembers} ${plan.activeMembers === 1 ? 'member keeps' : 'members keep'} the terms they bought.`
    : '';
  return (
    <FormSheet
      isOpen
      title={`Change ${plan.name}`}
      line={`For sales from now on.${keep} A new price is its price book item’s, kept in the price history.`}
      submitLabel="Save the plan"
      isDirty={JSON.stringify(f) !== JSON.stringify(first)}
      onClose={onClose}
      onSubmit={async () => {
        const problems = planProblems(f);
        if (problems.length) throw new Error(problems.join(' '));
        const update = planUpdate(plan, f);
        if (!update) throw new Error('Nothing changed yet.');
        try {
          await client.call('membershipTypes.update', update);
        } catch (e) {
          throw new Error(refusal(e), { cause: e });
        }
        onSaved(planSavedText(f.name.trim(), plan.activeMembers));
      }}
    >
      <TextField label="Plan name" maxLength={100} value={f.name} onChange={(name) => setF({ ...f, name })} />
      <TextField
        label="Price a term"
        inputMode="decimal"
        width="number"
        value={f.price}
        onChange={(price) => setF({ ...f, price })}
      />
      <TextField
        label="Term (months)"
        inputMode="numeric"
        width="number"
        value={f.term}
        onChange={(term) => setF({ ...f, term })}
      />
      <TextField
        label="Visits included"
        inputMode="numeric"
        width="number"
        value={f.visits}
        onChange={(visits) => setF({ ...f, visits })}
      />
      {Number(f.visits) > 0 && (
        <SelectField
          label="Visit type"
          value={f.jobType}
          options={[
            { value: '', label: 'Pick a job type…' },
            ...types.map((t) => ({ value: t.id, label: t.name })),
          ]}
          onChange={(jobType) => setF({ ...f, jobType })}
        />
      )}
      <TextField
        label="Member discount (%)"
        inputMode="decimal"
        width="number"
        value={f.discount}
        onChange={(discount) => setF({ ...f, discount })}
      />
      <SelectField
        label="At a cancel"
        description="What a member paid and hasn’t used yet, when their membership is cancelled. This is the company’s policy: it applies to every member of the plan who cancels, not only new sales."
        value={f.cancelRefund}
        options={CANCEL_OPTIONS}
        onChange={(v) => setF({ ...f, cancelRefund: v as CancelRefund })}
      />
      <Checkbox isSelected={f.active} onChange={(active) => setF({ ...f, active })}>
        Still sold (unticked, nobody new can buy it; members keep it)
      </Checkbox>
    </FormSheet>
  );
}

/** Every service in the price book, a page at a time: past the 200 a page gives (list caps M3). */
function useServices(): ServiceItem[] {
  const [items, setItems] = useState<ServiceItem[]>([]);
  useEffect(() => {
    let current = true;
    everyPage<ServiceItem, { name: string; id: string }>((after) =>
      client.call('pricebook.listItems', { type: 'service', limit: 200, ...(after ? { after } : {}) }),
    )
      .then((got) => current && setItems(got))
      // the picker stays empty, as it did when the one call failed; the plan can't be added without one
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, []);
  return items;
}

/** Adds a plan (membershipTypes.create), sold as a price book service: its price and tax. */
function NewPlanSheet({ onSaved, onClose }: { onSaved: (said: string) => void; onClose: () => void }) {
  const items = useServices();
  const types = useJobTypes();
  const blank = {
    name: '',
    itemId: '',
    term: '12',
    visits: '1',
    jobType: '',
    discount: '0',
    cancelRefund: 'optional' as CancelRefund,
  };
  const [f, setF] = useState(blank);
  const save = useSaveKey(JSON.stringify(f));
  const visits = Number(f.visits);
  const ready =
    !!f.name.trim() &&
    !!f.itemId &&
    Number.isInteger(Number(f.term)) &&
    Number(f.term) >= 1 &&
    Number.isInteger(visits) &&
    visits >= 0 &&
    (visits === 0 || !!f.jobType) &&
    /^\d{1,3}(\.\d{1,2})?$/.test(f.discount);
  return (
    <FormSheet
      isOpen
      title="A new plan"
      submitLabel="Add the plan"
      isDirty={JSON.stringify(f) !== JSON.stringify(blank)}
      isSubmitDisabled={!ready}
      onClose={onClose}
      onSubmit={async () => {
        const name = f.name.trim();
        try {
          await client.call(
            'membershipTypes.create',
            {
              name,
              pricebookItemId: f.itemId,
              termMonths: Number(f.term),
              visitsPerTerm: visits,
              ...(visits > 0 ? { visitJobTypeId: f.jobType } : {}),
              discountPct: f.discount,
              cancelRefund: f.cancelRefund,
            },
            { idempotencyKey: save.key },
          );
        } catch (e) {
          throw new Error(refusal(e), { cause: e });
        }
        save.renew();
        onSaved(`${name} added.`);
      }}
    >
      <TextField
        label="Plan name"
        value={f.name}
        placeholder="Maintenance plan"
        autoFocus
        onChange={(name) => setF({ ...f, name })}
      />
      <SelectField
        label="Sold as (its price and tax)"
        value={f.itemId}
        options={[
          { value: '', label: 'Pick a pricebook item…' },
          ...items.map((i) => ({ value: i.id, label: `${i.name} · ${(i.priceCents / 100).toFixed(2)}` })),
        ]}
        onChange={(itemId) => setF({ ...f, itemId })}
      />
      <TextField
        label="Term (months)"
        inputMode="numeric"
        width="number"
        value={f.term}
        onChange={(term) => setF({ ...f, term })}
      />
      <TextField
        label="Visits included"
        inputMode="numeric"
        width="number"
        value={f.visits}
        onChange={(v) => setF({ ...f, visits: v })}
      />
      {visits > 0 && (
        <SelectField
          label="Visit type"
          value={f.jobType}
          options={[
            { value: '', label: 'Pick a job type…' },
            ...types.map((t) => ({ value: t.id, label: t.name })),
          ]}
          onChange={(jobType) => setF({ ...f, jobType })}
        />
      )}
      <TextField
        label="Member discount (%)"
        inputMode="decimal"
        width="number"
        value={f.discount}
        onChange={(discount) => setF({ ...f, discount })}
      />
      <SelectField
        label="At a cancel"
        description="What a member paid and hasn’t used yet, when they cancel."
        value={f.cancelRefund}
        options={CANCEL_OPTIONS}
        onChange={(v) => setF({ ...f, cancelRefund: v as CancelRefund })}
      />
    </FormSheet>
  );
}
